import React, { useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
} from "@material-ui/core";
import {
    Collections,
    Infraction,
    InfractionDefaultValues,
    useSeasonOptions,
} from "api";
import CustomSelect from "ui/componentes/Form/SelectFieldFormik/CustomSelect";
import { FormButton, Message } from "ui/componentes";
import { useFirestore } from "react-redux-firebase";
import { useSnackbar } from "notistack";

interface CopyInfractionsFromSeasonProps {}

const useCopyInfractions = () => {
    const [copying, setCopying] = useState(false);
    const firestore = useFirestore();
    return {
        copyInfractions: (sourceSeasonId: string, targetSeasonId: string) => {
            if (!sourceSeasonId && !targetSeasonId) return Promise.reject();

            setCopying(true);

            return firestore
                .collection(Collections.INFRACTIONS)
                .where("seasonId", "==", targetSeasonId)
                .get()
                .then((snap) => snap.docs.forEach((doc) => doc.ref.delete()))
                .then(() => {
                    firestore
                        .collection(Collections.INFRACTIONS)
                        .where("seasonId", "==", sourceSeasonId)
                        .get()
                        .then((snap) =>
                            snap.docs.map<Infraction>(
                                (doc) => doc.data() as Infraction
                            )
                        )
                        .then((infractions) => {
                            infractions.forEach((infraction) =>
                                firestore
                                    .collection(Collections.INFRACTIONS)
                                    .add({
                                        ...InfractionDefaultValues,
                                        ...infraction,
                                        seasonId: targetSeasonId,
                                    })
                            );
                        });
                })
                .finally(() => setCopying(false));
        },
        copying,
    };
};

const CopyInfractionsFromSeason: React.FC<CopyInfractionsFromSeasonProps> = () => {
    const [open, setOpen] = useState(false);
    const [sourceSeason, setSourceSeason] = useState("");
    const [targetSeason, setTargetSeason] = useState("");
    const seasonOptions = useSeasonOptions();
    const seasonById = seasonOptions.reduce(
        (previousValue, currentValue) => ({
            ...previousValue,
            [currentValue.value]: currentValue.label,
        }),
        {}
    );
    const { copying, copyInfractions } = useCopyInfractions();
    const { enqueueSnackbar } = useSnackbar();

    return (
        <>
            <Button
                color="primary"
                onClick={() => setOpen(true)}
                fullWidth={false}
            >
                Copiar infrações
            </Button>
            <Dialog maxWidth="sm" open={open} fullWidth>
                <DialogTitle>Copiar infrações</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Message
                                variant="error"
                                show={!!sourceSeason && !!targetSeason}
                                message={`ATENÇÃO! Ao clicar em "Iniciar cópia", as infrações da temporada "${seasonById[targetSeason]}" serão substituídas com as infrações da temporada "${seasonById[sourceSeason]}".`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomSelect
                                label="Copiar infrações de"
                                options={seasonOptions}
                                onChange={(v) =>
                                    setSourceSeason(v.target.value)
                                }
                                value={sourceSeason}
                                style={{ marginRight: 5 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomSelect
                                label="Para"
                                options={seasonOptions}
                                onChange={(v) =>
                                    setTargetSeason(v.target.value)
                                }
                                value={targetSeason}
                                style={{ marginRight: 5 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Fechar</Button>
                    <FormButton
                        disabled={!sourceSeason && !targetSeason}
                        loading={copying}
                        color="primary"
                        onClick={() =>
                            copyInfractions(sourceSeason, targetSeason).then(
                                () => {
                                    enqueueSnackbar(
                                        "Dados copiados com sucesso!",
                                        { variant: "success" }
                                    );
                                }
                            )
                        }
                    >
                        Iniciar cópia
                    </FormButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { CopyInfractionsFromSeason };
